<template>
  <div class="wecarexm-ai-container">
    <!-- Background Effects -->
    <div class="background-effects">
      <div class="gradient-orb orb-1" />
      <div class="gradient-orb orb-2" />
      <div class="gradient-orb orb-3" />
      <div class="grid-pattern" />
    </div>

    <!-- Main Content -->
    <div class="main-content">
      <!-- Header -->
      <div class="ai-header">
        <div class="logo-section">
          <!--
            <div class="ai-icon">
            <VIcon
            size="32"
            color="white"
            >
            tabler-brain
            </VIcon>
            </div>
            <div class="logo-text">
            <h1 class="title">
            WecareXM AI
            </h1>
            <p class="subtitle">
            Intelligent Feedback Analysis
            </p>
            </div> 
          -->
          <!--
            <VNodeRenderer
            :nodes="logo"
            class="mb-6 me-auto"
            /> 
          -->

          <VImg
            :width="300"
            src="/logo/dark.svg"
          />
        </div>
      </div>

      <!-- Screen 1: Voice Recording -->
      <Transition
        name="fade-slide"
        mode="out-in"
      >
        <div
          v-if="currentScreen === 1"
          key="screen-1"
          class="screen-container"
        >
          <div class="screen-content">
            <div class="screen-header">
              <h2 class="screen-title">
                Share Your Feedback
              </h2>
              <p class="screen-description">
                Record your voice to help us understand your experience better
              </p>
            </div>

            <div class="voice-recorder-section">
              <div class="recorder-card">
                <VoiceRecorder
                  ref="voiceRecorderRef"
                  v-model="recordingData"
                  encoder="wav"
                  :max-duration="300"
                  @recording-complete="onRecordingComplete"
                />
              </div>
            </div>

            <div
              v-if="hasRecording"
              class="action-section"
            >
              <VBtn
                size="large"
                color="primary"
                class="submit-btn"
                @click="submitRecording"
              >
                <VIcon
                  start
                  icon="tabler-send"
                />
                Submit Feedback
              </VBtn>
            </div>
          </div>
        </div>

        <!-- Screen 2: Processing Animation -->
        <div
          v-else-if="currentScreen === 2"
          key="screen-2"
          class="screen-container"
        >
          <div class="screen-content processing-screen">
            <div class="processing-animation">
              <div class="pulse-circle">
                <div class="inner-pulse" />
                <VIcon
                  size="48"
                  color="white"
                >
                  {{ processingSteps[currentProcessingStep].icon }}
                </VIcon>
              </div>
            </div>

            <h2 class="processing-title">
              {{ processingSteps[currentProcessingStep].title }}
            </h2>
            <p class="processing-description">
              {{ processingSteps[currentProcessingStep].description }}
            </p>

            <!-- Progress Steps -->
            <div class="progress-steps">
              <div
                v-for="(step, index) in processingSteps"
                :key="index"
                class="progress-step"
                :class="{ active: index <= currentProcessingStep, current: index === currentProcessingStep }"
              >
                <div class="step-dot" />
                <span class="step-label">{{ step.label }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Screen 3: Streaming Text Display -->
        <div
          v-else-if="currentScreen === 3"
          key="screen-3"
          class="screen-container"
        >
          <div class="screen-content streaming-screen">
            <div class="screen-header">
              <h2 class="screen-title">
                Transcription Results
              </h2>
              <p class="screen-description">
                Your voice has been converted to text
              </p>
            </div>

            <div class="transcription-card">
              <div class="transcription-header">
                <VIcon
                  color="primary"
                  size="20"
                >
                  tabler-file-text
                </VIcon>
                <span class="transcription-label">Voice to Text</span>
                <VChip
                  v-if="isStreaming"
                  size="small"
                  color="success"
                  class="streaming-badge"
                >
                  <div class="streaming-dot" />
                  Live
                </VChip>
              </div>

              <div class="transcription-content">
                <p class="transcription-text">
                  {{ displayedTranscription }}
                  <span
                    v-if="isStreaming"
                    class="cursor-blink"
                  >|</span>
                </p>
              </div>
            </div>

            <!-- Show button when streaming is complete -->
            <Transition name="fade">
              <div
                v-if="!isStreaming && currentScreen === 3"
                class="action-section"
              >
                <VBtn
                  size="large"
                  color="primary"
                  class="generate-btn"
                  @click="generateTicket"
                >
                  <VIcon
                    start
                    icon="tabler-ticket"
                  />
                  Generate Ticket
                </VBtn>
              </div>
            </Transition>
          </div>
        </div>

        <!-- Screen 4: Generate Ticket (Merged with Screen 3) -->
        <!-- This screen is now part of Screen 3 -->

        <!-- Screen 5: Ticket Generation Loading -->
        <div
          v-else-if="currentScreen === 5"
          key="screen-5"
          class="screen-container"
        >
          <div class="screen-content processing-screen">
            <div class="processing-animation">
              <div class="pulse-circle ticket-pulse">
                <div class="inner-pulse" />
                <VIcon
                  size="48"
                  color="white"
                >
                  {{ ticketProcessingSteps[currentTicketProcessingStep].icon }}
                </VIcon>
              </div>
            </div>

            <h2 class="processing-title">
              {{ ticketProcessingSteps[currentTicketProcessingStep].title }}
            </h2>
            <p class="processing-description">
              {{ ticketProcessingSteps[currentTicketProcessingStep].description }}
            </p>

            <!-- Progress Steps -->
            <div class="progress-steps">
              <div
                v-for="(step, index) in ticketProcessingSteps"
                :key="index"
                class="progress-step"
                :class="{ active: index <= currentTicketProcessingStep, current: index === currentTicketProcessingStep }"
              >
                <div class="step-dot" />
                <span class="step-label">{{ step.label }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Screen 6: Generated Tickets Display -->
        <div
          v-else-if="currentScreen === 6"
          key="screen-6"
          class="screen-container"
        >
          <div class="screen-content tickets-screen">
            <div class="screen-header">
              <h2 class="screen-title">
                Generated Tickets
              </h2>
              <p class="screen-description">
                AI has analyzed your feedback and created the following tickets
              </p>
            </div>

            <div class="tickets-grid">
              <Transition
                v-for="(ticket, index) in generatedTickets"
                :key="ticket.id"
                name="ticket-appear"
                appear
                :style="{ transitionDelay: `${index * 150}ms` }"
              >
                <div class="ticket-card">
                  <div class="ticket-header">
                    <div class="ticket-id-section">
                      <VIcon
                        size="18"
                        color="primary"
                      >
                        tabler-ticket
                      </VIcon>
                      <span class="ticket-id">{{ ticket.id || `TCK-${index + 1}` }}</span>
                    </div>
                    <VChip
                      :color="getPriorityColor(ticket.priority)"
                      size="small"
                      class="priority-chip"
                    >
                      {{ ticket.priority }}
                    </VChip>
                  </div>

                  <div class="ticket-body">
                    <div class="ticket-row">
                      <span class="ticket-label">Type:</span>
                      <span class="ticket-value">{{ ticket.type }}</span>
                    </div>
                    <div class="ticket-row">
                      <span class="ticket-label">Department:</span>
                      <span class="ticket-value">{{ ticket.department }}</span>
                    </div>
                    <div class="ticket-row">
                      <span class="ticket-label">Category:</span>
                      <span class="ticket-value">{{ ticket.category }}</span>
                    </div>
                    <div class="ticket-row">
                      <span class="ticket-label">Sub Category:</span>
                      <span class="ticket-value">{{ ticket.subCategory }}</span>
                    </div>
                    <div class="ticket-description">
                      <span class="ticket-label">Description:</span>
                      <p class="ticket-value description-text">
                        {{ ticket.description }}
                      </p>
                    </div>
                  </div>

                  <div class="ticket-footer">
                    <VBtn
                      variant="tonal"
                      size="small"
                      color="primary"
                    >
                      View Details
                    </VBtn>
                  </div>
                </div>
              </Transition>
            </div>

            <div class="action-section">
              <VBtn
                variant="outlined"
                size="large"
                @click="resetFlow"
              >
                <VIcon
                  start
                  icon="tabler-refresh"
                />
                Start New Session
              </VBtn>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onUnmounted, ref } from 'vue'
import VoiceRecorder from '@/components/VoiceRecorder.vue'
import { VNodeRenderer } from "@layouts/components/VNodeRenderer"
import logo from '@images/logo.svg'

definePage({
  meta: {
    layout: "blank",
    public: true,
    unauthenticated: true,
  },
})

// Refs
const voiceRecorderRef = ref(null)
const currentScreen = ref(1)
const hasRecording = ref(false)
const recordingData = ref(null)

// Processing screen (Screen 2)
const currentProcessingStep = ref(0)

const processingSteps = [
  {
    icon: 'tabler-cloud-upload',
    title: 'Uploading Your Voice',
    description: 'Securely transferring your audio to our servers',
    label: 'Upload',
  },
  {
    icon: 'tabler-cpu',
    title: 'Processing Audio',
    description: 'Analyzing audio quality and preparing for conversion',
    label: 'Process',
  },
  {
    icon: 'tabler-access-point',
    title: 'Extracting Voice To Text',
    description: 'Converting your speech into written text using AI',
    label: 'Extract',
  },
]

// Streaming screen (Screen 3)
const isStreaming = ref(false)
const displayedTranscription = ref('')
const fullTranscription = ref('')
let streamingInterval = null

// Ticket generation screen (Screen 5)
const currentTicketProcessingStep = ref(0)

const ticketProcessingSteps = [
  {
    icon: 'tabler-cpu',
    title: 'Processing',
    description: 'Initializing AI analysis engine',
    label: 'Initialize',
  },
  {
    icon: 'tabler-zoom-scan',
    title: 'Analyzing Context',
    description: 'Understanding the context and sentiment of your feedback',
    label: 'Analyze',
  },
  {
    icon: 'tabler-bulb',
    title: 'Thinking Possibilities',
    description: 'Generating intelligent ticket recommendations',
    label: 'Generate',
  },
]

// Generated tickets (Screen 6)
const generatedTickets = ref([])

// Methods
const onRecordingComplete = data => {
  hasRecording.value = true
  
  // recordingData.value = data

  console.log('Recording completed:', data)
}

const isProcessingAudio = ref(false)

const submitRecording = async () => {
  if (!hasRecording.value) return

  // Move to processing screen
  currentScreen.value = 2
  currentProcessingStep.value = 0
  isProcessingAudio.value = true

  const formData = convertToFormData({
    audio_file: recordingData.value,
  })

  // Call API To Process Voice Recording
  $api('/wecarexm-ai/voice-to-text', {
    method: 'POST',
    headers: {
      ContentType: 'multipart/form-data',
    },
    body: formData,
  }).then(({ data }) => {
    fullTranscription.value = data.speech_to_text_response
    isProcessingAudio.value = false
  }).catch(error => {
    isProcessingAudio.value = false
    resetFlow()
  })

  // Simulate processing steps
  await simulateProcessing()
}

const simulateProcessing = async () => {
  for (let i = 0; i < processingSteps.length; i++) {
    currentProcessingStep.value = i
  
    // If we're at step 2, wait for audio processing to complete
    if (i === 2) {
      while (isProcessingAudio.value === true) {
        await delay(500)
      }
    }
    
    await delay(2000)
  }

  if (currentScreen.value === 2) {
    // Move to streaming screen
    currentScreen.value = 3
    await nextTick()
    startStreaming()
  }
}

const startStreaming = () => {
  isStreaming.value = true
  displayedTranscription.value = ''

  let charIndex = 0
  streamingInterval = setInterval(() => {
    if (charIndex < fullTranscription.value.length) {
      displayedTranscription.value += fullTranscription.value[charIndex]
      charIndex++
    } else {
      isStreaming.value = false
      clearInterval(streamingInterval)
      streamingInterval = null
    }
  }, 30) // Adjust speed here (30ms per character)
}

const isProcessingTicketGeneration = ref(false)

const generateTicket = async () => {
  // Move to ticket generation screen
  currentScreen.value = 5
  currentTicketProcessingStep.value = 0

  isProcessingTicketGeneration.value = true

  // Call API To Generate Tickets
  $api('/wecarexm-ai/ticket-generation', {
    method: 'POST',
    body: {
      feedback: fullTranscription.value,
    },
  }).then(({ data }) => {
    generatedTickets.value = data.tickets
    isProcessingTicketGeneration.value = false
  }).catch(error => {
    isProcessingTicketGeneration.value = false
    resetFlow()
  })

  // Simulate ticket processing steps
  await simulateTicketProcessing()
}

const simulateTicketProcessing = async () => {
  for (let i = 0; i < ticketProcessingSteps.length; i++) {
    currentTicketProcessingStep.value = i
  
    // If we're at step 2, wait for audio processing to complete
    if (i === 2) {
      while (isProcessingTicketGeneration.value === true) {
        await delay(500)
      }
    }
    
    await delay(2000)
  }

  console.log(currentScreen.value)

  if (currentScreen.value === 5) {
    // Move to generated tickets screen
    currentScreen.value = 6
  }
}

const getPriorityColor = priority => {
  const colors = {
    High: 'error',
    Medium: 'warning',
    Low: 'success',
  }

  return colors[priority] || 'default'
}

const resetFlow = () => {
  currentScreen.value = 1
  hasRecording.value = false
  recordingData.value = null
  displayedTranscription.value = ''
  currentProcessingStep.value = 0
  currentTicketProcessingStep.value = 0

  if (streamingInterval) {
    clearInterval(streamingInterval)
    streamingInterval = null
  }

  // Clear the voice recorder
  if (voiceRecorderRef.value) {
    voiceRecorderRef.value.clearRecording()
  }
}

const delay = ms => new Promise(resolve => setTimeout(resolve, ms))

// Cleanup
onUnmounted(() => {
  if (streamingInterval) {
    clearInterval(streamingInterval)
  }
})

// TODO: API Integration Points
// 1. submitRecording() - Send recordingData.value to backend API
// 2. After API response, update fullTranscription.value with actual transcription
// 3. generateTicket() - Send displayedTranscription.value to ticket generation API
// 4. After API response, update generatedTickets.value with actual tickets
</script>

<style lang="scss" scoped>
.wecarexm-ai-container {
  position: relative;
  min-height: 100vh;
  width: 100%;
  background: linear-gradient(135deg, #0a0e27 0%, #1a1f3a 50%, #0f1629 100%);
  overflow: hidden;
}

// Background Effects
.background-effects {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
}

.gradient-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.3;
  animation: float 20s ease-in-out infinite;
}

.orb-1 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, #00d4ff 0%, transparent 70%);
  top: -10%;
  left: -10%;
  animation-delay: 0s;
}

.orb-2 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, #0ea5e9 0%, transparent 70%);
  bottom: -15%;
  right: -15%;
  animation-delay: -7s;
}

.orb-3 {
  width: 350px;
  height: 350px;
  background: radial-gradient(circle, #06b6d4 0%, transparent 70%);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation-delay: -14s;
}

.grid-pattern {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image:
    linear-gradient(rgba(6, 182, 212, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(6, 182, 212, 0.03) 1px, transparent 1px);
  background-size: 50px 50px;
}

@keyframes float {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(30px, -30px) scale(1.1);
  }
  66% {
    transform: translate(-20px, 20px) scale(0.9);
  }
}

// Main Content
.main-content {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
}

// Header
.ai-header {
  display: flex;
  justify-content: center;
  // margin-bottom: 3rem;
  padding: 1.5rem 0;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.ai-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 32px rgba(0, 212, 255, 0.3);
}

.logo-text {
  .title {
    font-size: 2rem;
    font-weight: 700;
    background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0;
    line-height: 1;
  }

  .subtitle {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.875rem;
    margin: 0.25rem 0 0 0;
  }
}

// Screen Container
.screen-container {
  width: 100%;
  min-height: 500px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.screen-content {
  width: 100%;
  max-width: 800px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  padding: 3rem;
  backdrop-filter: blur(20px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.screen-header {
  text-align: center;
  margin-bottom: 3rem;

  .screen-title {
    font-size: 2rem;
    font-weight: 600;
    color: white;
    margin: 0 0 0.5rem 0;
  }

  .screen-description {
    color: rgba(255, 255, 255, 0.6);
    font-size: 1rem;
    margin: 0;
  }
}

// Voice Recorder Section
.voice-recorder-section {
  display: flex;
  justify-content: center;
  margin: 3rem 0;
}

.recorder-card {
  width: 100%;
  max-width: 400px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
}

// Action Section
.action-section {
  display: flex;
  justify-content: center;
  margin-top: 2rem;

  .submit-btn,
  .generate-btn {
    min-width: 200px;
    font-weight: 600;
    letter-spacing: 0.5px;
    box-shadow: 0 4px 16px rgba(14, 165, 233, 0.3);
  }
}

// Processing Screen
.processing-screen {
  text-align: center;
}

.processing-animation {
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
}

.pulse-circle {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  animation: pulse-scale 2s ease-in-out infinite;
  box-shadow: 0 8px 32px rgba(0, 212, 255, 0.4);

  .inner-pulse {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 2px solid rgba(0, 212, 255, 0.6);
    animation: pulse-ring 2s ease-out infinite;
  }
}

.ticket-pulse {
  background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
  box-shadow: 0 8px 32px rgba(139, 92, 246, 0.4);

  .inner-pulse {
    border-color: rgba(139, 92, 246, 0.6);
  }
}

@keyframes pulse-scale {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

@keyframes pulse-ring {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
}

.processing-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: white;
  margin: 0 0 0.5rem 0;
}

.processing-description {
  color: rgba(255, 255, 255, 0.6);
  font-size: 1rem;
  margin: 0 0 3rem 0;
}

// Progress Steps
.progress-steps {
  display: flex;
  justify-content: center;
  gap: 3rem;
  margin-top: 3rem;
}

.progress-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  opacity: 0.3;
  transition: opacity 0.3s ease;

  &.active {
    opacity: 1;
  }

  &.current .step-dot {
    animation: dot-pulse 1.5s ease-in-out infinite;
  }
}

.step-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
  box-shadow: 0 2px 8px rgba(0, 212, 255, 0.5);
}

@keyframes dot-pulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 2px 8px rgba(0, 212, 255, 0.5);
  }
  50% {
    transform: scale(1.3);
    box-shadow: 0 4px 16px rgba(0, 212, 255, 0.8);
  }
}

.step-label {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
  font-weight: 500;
}

// Streaming Screen
.streaming-screen {
  .transcription-card {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding: 1.5rem;
    margin-bottom: 2rem;
  }

  .transcription-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  .transcription-label {
    color: rgba(255, 255, 255, 0.8);
    font-weight: 500;
    flex: 1;
  }

  .streaming-badge {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .streaming-dot {
    margin-right: 8px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    animation: blink 1.5s ease-in-out infinite;
  }

  @keyframes blink {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.3;
    }
  }

  .transcription-content {
    min-height: 150px;
  }

  .transcription-text {
    color: rgba(255, 255, 255, 0.9);
    font-size: 1.125rem;
    line-height: 1.8;
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
  }

  .cursor-blink {
    color: #00d4ff;
    animation: cursor-blink 1s step-end infinite;
    font-weight: 100;
  }

  @keyframes cursor-blink {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0;
    }
  }
}

// Tickets Screen
.tickets-screen {
  .tickets-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: 1.5rem;
    margin-bottom: 2rem;
  }

  .ticket-card {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding: 1.5rem;
    transition: transform 0.3s ease, box-shadow 0.3s ease;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 32px rgba(0, 212, 255, 0.2);
      border-color: rgba(0, 212, 255, 0.3);
    }
  }

  .ticket-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  .ticket-id-section {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .ticket-id {
    color: white;
    font-weight: 600;
    font-family: 'Roboto Mono', monospace;
  }

  .priority-chip {
    font-weight: 600;
  }

  .ticket-body {
    margin-bottom: 1rem;
  }

  .ticket-row {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }

  .ticket-description {
    margin-top: 1rem;
  }

  .ticket-label {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.875rem;
    font-weight: 500;
    min-width: 110px;
  }

  .ticket-value {
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.875rem;
    flex: 1;

    &.description-text {
      margin: 0.5rem 0 0 0;
      line-height: 1.6;
      display: block;
    }
  }

  .ticket-footer {
    display: flex;
    justify-content: flex-end;
    padding-top: 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }
}

// Transitions
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.5s ease;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(30px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.ticket-appear-enter-active {
  transition: all 0.6s ease;
}

.ticket-appear-enter-from {
  opacity: 0;
  transform: translateY(30px) scale(0.95);
}

// Responsive
@media (max-width: 768px) {
  .main-content {
    padding: 1rem;
  }

  .screen-content {
    padding: 2rem 1.5rem;
  }

  .screen-header .screen-title {
    font-size: 1.5rem;
  }

  .progress-steps {
    gap: 1.5rem;
  }

  .tickets-grid {
    grid-template-columns: 1fr;
  }

  .logo-text .title {
    font-size: 1.5rem;
  }
}

@media (max-width: 480px) {
  .screen-content {
    padding: 1.5rem 1rem;
  }

  .processing-title {
    font-size: 1.5rem;
  }

  .progress-steps {
    flex-direction: column;
    gap: 1rem;
  }

  .ai-header {
    margin-bottom: 2rem;
  }
}
</style>
